

import { useEffect, useState } from 'react'
import { useOrganizations } from '@/hooks/useOrganizations'
import { useWorkspaces } from '@/hooks/useWorkspaces'
import { backendApi } from '@/lib/api/backend'
import { LoadingState } from '@/components/ui/LoadingState'

interface NavigationProviderProps {
  children: React.ReactNode
}

/**
 * NavigationProvider initializes organizations and workspaces from the database
 * and syncs them with Zustand stores.
 */
export function NavigationProvider({ children }: NavigationProviderProps) {
  const { setOrganizations, setCurrentOrg, currentOrg } = useOrganizations()
  const { setWorkspaces, setCurrentWorkspace } = useWorkspaces()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const initializeNavigation = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Middleware has already validated authentication
        // We can safely fetch organizations directly
        const response = await backendApi.organizations.list()
        const organizations = Array.isArray(response) ? response : []

        if (organizations.length > 0) {
          // Pass full CID-generated Organization objects to the store
          setOrganizations(organizations)

          // Set current org from localStorage or use first org
          const lastOrgId =
            typeof window !== 'undefined'
              ? localStorage.getItem('lastOrganizationId')
              : null

          const orgToSet =
            organizations.find((o: { id: string }) => o.id === lastOrgId) || organizations[0]

          if (orgToSet) {
            setCurrentOrg(orgToSet)
          }
        } else {
          console.warn('[NavigationProvider] No organizations found')
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load organizations'
        console.error('[NavigationProvider] Error initializing navigation:', error)
        setError(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }

    initializeNavigation()
  }, [setOrganizations, setCurrentOrg])

  // Fetch workspaces when organization changes
  useEffect(() => {
    const fetchWorkspaces = async () => {
      if (!currentOrg) return

      try {
        const response = await backendApi.workspaces.list()
        const allWorkspaces = Array.isArray(response) ? response : []

        const orgWorkspaces = allWorkspaces.filter(
          (w) => w.organizationId === currentOrg.id
        )

        if (orgWorkspaces) {
          // Pass full CID-generated Workspace objects to the store
          setWorkspaces(orgWorkspaces)

          // Set current workspace from localStorage or use first workspace
          const lastWorkspaceId =
            typeof window !== 'undefined'
              ? localStorage.getItem('lastWorkspaceId')
              : null

          let workspaceToSet = null
          if (lastWorkspaceId) {
            workspaceToSet = orgWorkspaces.find((w: { id: string }) => w.id === lastWorkspaceId)
            // Verify it belongs to current org
            if (workspaceToSet && workspaceToSet.organizationId !== currentOrg.id) {
              workspaceToSet = null
            }
          }

          // Fallback to first workspace if no valid lastWorkspaceId
          if (!workspaceToSet && orgWorkspaces.length > 0) {
            workspaceToSet = orgWorkspaces[0]
          }

          if (workspaceToSet) {
            setCurrentWorkspace(workspaceToSet)
          }
      }
      } catch (error) {
        console.error('Error fetching workspaces:', error)
      }
    }

    fetchWorkspaces()
  }, [currentOrg, setWorkspaces, setCurrentWorkspace])

  // Show loading state
  if (isLoading) {
    return <LoadingState message="Loading organizations..." fullScreen />
  }

  // Show error state
  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="text-red-600 dark:text-red-400 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Failed to Load Organizations
          </h2>
          <p className="text-sm text-gray-600 dark:text-zinc-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
